import { DownloadSimpleIcon, EyeIcon, FilePdfIcon } from "@phosphor-icons/react";

import type { SafetyPassportFile } from "@miter/types/contracts";
import { formatDateString } from "@miter/utils/date-utils";
import {
  AccordionContent,
  AccordionHeaderRow,
  AccordionItem,
  AccordionTrigger,
  ActionModal,
  ActionModalBody,
  ActionModalContent,
  ActionModalFooter,
  ActionModalHeader,
  ActionModalTitle,
  ActionModalTrigger,
  Badge,
  SimpleTable,
  SimpleTableBody,
  SimpleTableRow,
  SimpleTableCell,
  Button,
  cn,
} from "ui2";

import type { SafetyPassportCertification } from "../../../packages/types/src/contracts/safety-passport";

const DATE_FORMAT = "MMMM d, yyyy";

const formatCertExpiryDate = (expiresAt?: string | null): string =>
  expiresAt ? formatDateString(expiresAt, DATE_FORMAT) : "No expiry";

type CertificationDetailsTableProps = {
  cert: SafetyPassportCertification;
};

const CertificationDetailsTable = ({ cert }: CertificationDetailsTableProps): React.JSX.Element => {
  const isActive = cert.status === "active";

  return (
    <SimpleTable className="tw:text-sm tw:font-normal tw:w-72 tw:[&_tr:last-child>td]:pb-0">
      <SimpleTableBody>
        {cert.custom_fields.map((field) => (
          <SimpleTableRow key={field.label} className="tw:border-none">
            <SimpleTableCell className="tw:font-medium">{field.label}</SimpleTableCell>
            <SimpleTableCell className="tw:text-midnight/70">{field.value}</SimpleTableCell>
          </SimpleTableRow>
        ))}
        <SimpleTableRow className="tw:border-none">
          <SimpleTableCell className="tw:font-medium">Expiration date</SimpleTableCell>
          <SimpleTableCell className={isActive ? "tw:text-midnight/70" : "tw:text-secondary-clay"}>
            {formatCertExpiryDate(cert.expires_at)}
          </SimpleTableCell>
        </SimpleTableRow>
      </SimpleTableBody>
    </SimpleTable>
  );
};

const FilePreview = ({
  preview,
  name,
}: {
  preview: NonNullable<SafetyPassportFile["preview"]>;
  name: string;
}) => {
  if (preview.kind === "image") {
    return <img src={preview.url} alt={name} className="tw:size-full tw:bg-white tw:object-contain" />;
  }

  return (
    <object data={preview.url} type="application/pdf" aria-label={name} className="tw:h-full tw:w-full">
      <div className="tw:flex tw:items-center tw:gap-2 tw:p-3 tw:text-sm tw:text-midnight/60">
        <FilePdfIcon size={20} />
        Preview isn&apos;t available on this device. Use Download to view it.
      </div>
    </object>
  );
};

const DownloadButton = ({ file }: { file: SafetyPassportFile }): React.JSX.Element => (
  <Button asChild variant="outline">
    <a href={file.url} target="_blank" rel="noopener noreferrer">
      <DownloadSimpleIcon weight="bold" />
      Download
    </a>
  </Button>
);

const CertificationFile = ({ file }: { file: SafetyPassportFile }): React.JSX.Element => (
  <div className="tw:flex tw:items-center tw:justify-between tw:gap-3">
    <span className="tw:min-w-0 tw:flex-1 tw:truncate tw:text-sm tw:text-midnight/70">{file.name}</span>
    {file.preview ? (
      <ActionModal>
        <ActionModalTrigger asChild>
          <Button variant="secondary">
            <EyeIcon weight="bold" />
            View
          </Button>
        </ActionModalTrigger>
        {/* Re-declared locally: this app's own tw:inset-0 otherwise outranks ui2's prebuilt md: positioning. */}
        <ActionModalContent size="lg" className="tw:md:inset-auto tw:md:top-1/2 tw:md:left-1/2">
          <ActionModalHeader>
            <ActionModalTitle>{file.name}</ActionModalTitle>
          </ActionModalHeader>
          <ActionModalBody className="tw:p-0 tw:md:h-[60dvh] tw:md:flex-none">
            <FilePreview preview={file.preview} name={file.name} />
          </ActionModalBody>
          <ActionModalFooter>
            <DownloadButton file={file} />
          </ActionModalFooter>
        </ActionModalContent>
      </ActionModal>
    ) : (
      <DownloadButton file={file} />
    )}
  </div>
);

const CertificationAccordionItem = ({ cert }: { cert: SafetyPassportCertification }): React.JSX.Element => {
  const isActive = cert.status === "active";
  const expiryLabel = cert.expires_at
    ? `${isActive ? "Expires" : "Expired"} on ${formatCertExpiryDate(cert.expires_at)}`
    : "No expiry";

  return (
    <AccordionItem key={cert.certification_type_id} value={cert.certification_type_id}>
      <AccordionHeaderRow>
        <AccordionTrigger
          subtitle={isActive ? expiryLabel : <span className="tw:text-secondary-clay">{expiryLabel}</span>}
          className="tw tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2"
        >
          {cert.title}
        </AccordionTrigger>
        <Badge
          className={cn({
            "tw:bg-secondary-mint/70 tw:text-secondary-moss": isActive,
            "tw:bg-secondary-peach/70 tw:text-secondary-clay": !isActive,
          })}
        >
          {cert.status.toUpperCase()}
        </Badge>
      </AccordionHeaderRow>
      <AccordionContent className="tw:border-t tw:border-stroke tw:mt-4">
        <CertificationDetailsTable cert={cert} />
        {cert.files.length > 0 && (
          <div className="tw:mt-4 tw:flex tw:flex-col tw:gap-3">
            {cert.files.map((file) => (
              <CertificationFile key={file.url} file={file} />
            ))}
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};

export default CertificationAccordionItem;
